import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { Browser } from "@capacitor/browser";
import { isNativeApp } from "@/lib/nativeApp";
import { captureError } from "@/utils/captureError";
import {
  setLinkingIntent,
  getLinkingIntent,
  clearLinkingIntent,
  isLinkingInProgress as isLinkingInProgressIntent,
} from "@/utils/linkingIntent";
import type { Database } from "@/integrations/supabase/types";

type AuthMethodType = Database["public"]["Enums"]["auth_method_type"];

export interface LinkedAccount {
  id: string;
  method_type: AuthMethodType;
  identifier: string;
  is_primary: boolean;
  is_verified: boolean;
  last_used_at: string | null;
  created_at: string;
}

function providerToMethodType(provider: "google" | "apple"): AuthMethodType {
  return provider === "google" ? "google_oauth" : "apple_oauth";
}

async function upsertOAuthMethod(
  userId: string,
  provider: "google" | "apple",
  identifier: string
): Promise<void> {
  const { error } = await supabase
    .from("auth_methods")
    .upsert(
      {
        user_id: userId,
        method_type: providerToMethodType(provider),
        identifier,
        is_verified: true,
        is_primary: false,
      },
      { onConflict: "user_id,method_type" }
    );

  if (error) throw error;
}

async function syncOAuthMethodsFromUser(user: any): Promise<void> {
  const identities = user?.identities || [];
  const tasks: Promise<void>[] = [];

  for (const identity of identities) {
    if (identity?.provider !== "google" && identity?.provider !== "apple") continue;

    const provider = identity.provider as "google" | "apple";
    const email =
      identity?.identity_data?.email ||
      user.email ||
      `${provider} account`;

    tasks.push(upsertOAuthMethod(user.id, provider, String(email).trim().toLowerCase()));
  }

  if (tasks.length > 0) {
    await Promise.all(tasks);
  }
}

// ─── Session-swap OAuth linking ──────────────────────────────────────────

/**
 * Initiate linking a social account by saving the current session
 * and redirecting to OAuth. On return, completeSocialLink() handles the rest.
 *
 * The linking state (provider + original session) is persisted via the shared
 * `linkingIntent` helper (sessionStorage + localStorage + 10-min TTL) so it
 * survives the native OAuth round-trip, where the app WebView can be reclaimed
 * while backgrounded — the same reason `oauthIntent.ts` dual-writes.
 *
 * Native (Android app): opens Google/Apple in a Chrome Custom Tab and returns
 * via the `com.behtari.app://login` deep link — mirroring Login.tsx's native
 * OAuth path. The linking-in-progress flag (NOT the deep-link path) is what
 * distinguishes a linking return from a normal login return, so the existing
 * single AndroidManifest intent-filter serves both. The old unconditional
 * `lovable.auth.signInWithOAuth(...)` resolved `redirect_uri` to https://localhost
 * on native — an unroutable destination — which was the "404 page not found"
 * that this branch fixes.
 *
 * Web: unchanged — `lovable.auth.signInWithOAuth` with the current origin.
 */
export async function initiateSocialLink(provider: "google" | "apple"): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("Not authenticated");

  // Save current session info for restoration after OAuth (dual-store + TTL).
  setLinkingIntent({
    provider,
    userId: session.user.id,
    refreshToken: session.refresh_token,
  });

  if (isNativeApp()) {
    // Native path: open the provider in a Chrome Custom Tab; the deep-link
    // return is handled by App.tsx's appUrlOpen listener, which sees the
    // linking flag and routes through completeSocialLink().
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: "com.behtari.app://login",
          skipBrowserRedirect: true,
        },
      });
      if (error || !data?.url) throw error || new Error("No OAuth URL returned");
      await Browser.open({ url: data.url });
    } catch (err) {
      clearLinkingIntent();
      captureError("oauth-link-native", err, { stage: "initiate", provider });
      throw err;
    }
    return;
  }

  // Web path — unchanged. Trigger OAuth; this redirects away.
  try {
    const { error } = await lovable.auth.signInWithOAuth(provider, {
      redirect_uri: window.location.origin,
    });
    if (error) throw error;
  } catch (err) {
    clearLinkingIntent();
    captureError("oauth-link-native", err, { stage: "initiate-web", provider });
    throw err;
  }
}

/**
 * Check if we're in a linking flow (called from onAuthStateChange + App.tsx).
 */
export function isLinkingInProgress(): boolean {
  return isLinkingInProgressIntent();
}

/**
 * Complete the linking flow after OAuth redirect.
 * Captures the provider email, signs out the OAuth session,
 * restores the original session, and calls the edge function.
 */
export async function completeSocialLink(): Promise<{ success: boolean; provider?: string; email?: string; error?: string }> {
  const intent = getLinkingIntent();

  // Clear flags immediately to prevent re-entry (both stores).
  clearLinkingIntent();

  if (!intent) {
    // Every other early-return in this function is now instrumented (see
    // below) — this one included, since "the flag was already gone by the
    // time completeSocialLink ran" is itself a distinct, previously-silent
    // failure mode worth distinguishing from the others.
    captureError("oauth-link-native", new Error("completeSocialLink: no linking intent found"), { stage: "no-intent" });
    return { success: false, error: "No linking session found" };
  }

  const provider = intent.provider;
  const savedSession = { userId: intent.userId, refreshToken: intent.refreshToken };

  try {
    // 1. Get the OAuth user's email from the current (new) session
    const { data: { user: oauthUser } } = await supabase.auth.getUser();
    if (!oauthUser) {
      captureError("oauth-link-native", new Error("completeSocialLink: no OAuth user after redirect"), { stage: "no-oauth-user", provider });
      return { success: false, error: "No OAuth user found after redirect" };
    }

    // Idempotency / stale-intent guard: a genuine linking return puts us on the
    // fresh OAuth identity, which is a DIFFERENT auth user than the original CNIC
    // account we saved. If the current user IS the saved user, there is no OAuth
    // identity to capture (the link already completed, or this is a stale intent
    // firing on a normal login) — bail WITHOUT mutating the session so we never
    // sign out / restore against the wrong account.
    if (oauthUser.id === savedSession.userId) {
      captureError("oauth-link-native", new Error("completeSocialLink: current user already the original user"), { stage: "already-original-user", provider });
      return { success: false, error: "No OAuth session to link (already original user)" };
    }

    const providerEmail = oauthUser.email;
    const orphanUserId = oauthUser.id;

    if (!providerEmail) {
      captureError("oauth-link-native", new Error("completeSocialLink: OAuth provider returned no email"), { stage: "no-provider-email", provider, orphanUserId });
      return { success: false, error: "Could not retrieve email from OAuth provider" };
    }

    // 1b. Capture the OAuth session's access token BEFORE signing out. The edge
    // function verifies this token server-side and derives the identity from it,
    // so an attacker who merely knows a victim's auth-user UUID can no longer
    // plant that victim's email against their own account. `signOut({ scope:
    // 'local' })` below only clears client storage — it does not revoke the
    // token — so it stays valid for the verification call in step 4.
    const { data: { session: oauthSession } } = await supabase.auth.getSession();
    const orphanAccessToken = oauthSession?.access_token;

    if (!orphanAccessToken) {
      captureError("oauth-link-native", new Error("completeSocialLink: no OAuth access token to prove identity"), { stage: "no-oauth-token", provider, orphanUserId });
      return { success: false, error: "Could not verify the OAuth session" };
    }

    // 2. Sign out the OAuth session (local only)
    await supabase.auth.signOut({ scope: "local" });

    // 3. Restore original session
    const { error: refreshError } = await supabase.auth.refreshSession({
      refresh_token: savedSession.refreshToken,
    });

    if (refreshError) {
      // Most likely culprit if this fires: the CNIC session's refresh token
      // captured at initiateSocialLink() time got rotated/invalidated during
      // the OAuth round-trip (e.g. an auto-refresh tick on app resume, see
      // nativeRealtimeLifecycle's startAutoRefresh()) before this saved copy
      // was used. Previously this returned silently with zero telemetry.
      captureError("oauth-link-native", refreshError, { stage: "restore-session-failed", provider, orphanUserId });
      return { success: false, error: "Failed to restore original session: " + refreshError.message };
    }

    // 4. Call edge function to store association + cleanup orphan
    const normalizedProviderEmail = providerEmail.trim().toLowerCase();
    const { data, error: fnError } = await supabase.functions.invoke(
      "link-social-account",
      {
        body: {
          originalUserId: savedSession.userId,
          providerEmail: normalizedProviderEmail,
          provider,
          // Sent so the edge function can independently verify (via
          // auth.admin.getUserById) that the provider email is a REAL verified
          // email owned by this OAuth account — never trusting the body string.
          orphanUserId,
          // Proof of POSSESSION of that OAuth identity, not just knowledge of
          // its id. The function resolves the identity from this token via
          // supabase auth and rejects the request if it does not match.
          orphanAccessToken,
        },
      }
    );

    // 4b. Sync biometric token with restored session to prevent "session expired"
    try {
      const { data: restoredSession } = await supabase.auth.getSession();
      if (restoredSession?.session?.user && restoredSession.session.refresh_token) {
        const { syncBiometricSessionToken } = await import('@/services/biometricService');
        syncBiometricSessionToken(restoredSession.session.user.id, restoredSession.session.refresh_token);
      }
    } catch {
      // best-effort biometric sync
    }

    if (fnError) {
      captureError("oauth-link-native", fnError, { stage: "link-edge-fn", provider });
      return { success: false, error: fnError.message };
    }

    if (data && !data.success) {
      captureError("oauth-link-native", new Error(data.error || "edge fn returned success:false"), { stage: "link-edge-fn-result", provider });
      return { success: false, error: data.error || "Unknown error from edge function" };
    }

    return { success: true, provider, email: providerEmail };
  } catch (err: any) {
    captureError("oauth-link-native", err, { stage: "complete", provider });
    // Attempt to restore original session even on error
    try {
      await supabase.auth.refreshSession({
        refresh_token: savedSession.refreshToken,
      });
    } catch (restoreErr) {
      captureError("oauth-link-native", restoreErr, { stage: "complete-restore", provider });
    }
    return { success: false, error: err.message || "Linking failed" };
  }
}

// ─── Existing functions ──────────────────────────────────────────────────

/**
 * Fetch all linked auth methods for the current user
 */
export async function getLinkedAccounts(
  opts: { syncFromIdentities?: boolean } = {}
): Promise<LinkedAccount[]> {
  // Resolve the user from the LOCAL session first. `getUser()` is a live GoTrue
  // round-trip; on the native WebView (and immediately after a cache clear,
  // while the session is still hydrating) it can transiently return null or
  // fail outright. That threw "Not authenticated" here, and the caller rendered
  // the empty result as "Not connected" — showing a genuinely-linked Google
  // account as gone while the auth_methods row was perfectly intact in the DB.
  // getSession() reads the persisted session with no network dependency.
  const { data: { session } } = await supabase.auth.getSession();
  let user = session?.user ?? null;
  if (!user) {
    const { data } = await supabase.auth.getUser();
    user = data.user ?? null;
  }
  if (!user) throw new Error("Not authenticated");

  // Keep auth_methods in sync with linked OAuth identities.
  //
  // This is a WRITE inside a read, and it is the reason "Disconnect" appeared
  // to do nothing (2026-07-30). Every caller of this function re-UPSERTS a row
  // for every OAuth identity still attached to the GoTrue user — so the unlink
  // flow deleted the row and then immediately resurrected it on its own
  // refresh. The disconnect is only real once the IDENTITY is gone, which is
  // why unlinkSocialAccount now removes that first and reads with sync off.
  if (opts.syncFromIdentities !== false) {
    await syncOAuthMethodsFromUser(user).catch(() => {});
  }

  const { data, error } = await supabase
    .from("auth_methods")
    .select("*")
    .eq("user_id", user.id)
    .order("is_primary", { ascending: false });

  if (error) throw error;
  return (data || []) as LinkedAccount[];
}

/**
 * Associate a social email with the current user at the application level.
 */
export async function connectSocialAccountByEmail(
  provider: "google" | "apple",
  email: string
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");

  await upsertOAuthMethod(user.id, provider, email);
}

/**
 * Record a social identity in the auth_methods table after successful OAuth linking.
 */
export async function syncLinkedAccount(
  provider: "google" | "apple",
  email: string
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");

  await upsertOAuthMethod(user.id, provider, email);
}

/**
 * Unlink a social account. Cannot unlink primary method.
 */
export async function unlinkSocialAccount(
  provider: "google" | "apple"
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");

  const methodType: AuthMethodType = providerToMethodType(provider);

  // ── Why this was rewritten (2026-07-30) ────────────────────────────────
  // Disconnect reported success and changed nothing. Three independent
  // defects, all confirmed against the live database:
  //
  //  1. THE GUARD COUNTED THE WRONG THING. It required >1 `auth_methods` row,
  //     but that table is a mirror, not the credential store: every account
  //     checked (incl. Majid Ali Waryam, who has a CNIC *and* a password) has
  //     exactly ONE row — the google_oauth one — because `ensurePrimaryMethod`
  //     only ever backfills a `cnic_password` row when it runs, and its caller
  //     swallows its errors. So the guard tripped for everyone and the delete
  //     below was never reached.
  //  2. THE DELETE COULD NOT DETECT DOING NOTHING. `.delete()` with no
  //     `.select()` returns error === null for a zero-row delete — the exact
  //     RLS silent-fail CLAUDE.md §12 warns about. (RLS itself is fine here:
  //     `Users can manage own auth methods` is FOR ALL with
  //     auth.uid() = user_id, so a real delete is permitted.)
  //  3. IT WAS SELF-DEFEATING ANYWAY. `getLinkedAccounts()` re-upserts a row
  //     for every OAuth identity still attached in GoTrue, so even a
  //     successful delete was undone by the caller's own refresh.
  //
  // The mapping row is downstream of the GoTrue identity, so the identity is
  // what has to go — otherwise Google sign-in still resolves to this account
  // and the row legitimately comes back. Read with sync OFF so we never
  // re-create what we are about to remove.
  const accounts = await getLinkedAccounts({ syncFromIdentities: false });
  const target = accounts.find((a) => a.method_type === methodType);
  if (!target) throw new Error("Account not linked");
  if (target.is_primary) throw new Error("Cannot remove primary login method");

  // Real safety check: would this leave the user with no way back in? Ask
  // GoTrue, which knows the actual credentials, instead of counting mirror
  // rows. Supabase refuses to unlink the last identity itself, so this is a
  // clearer message rather than a new rule.
  const identities = user.identities ?? [];
  const targetIdentity = identities.find((i) => i.provider === provider);

  if (targetIdentity && identities.length <= 1) {
    throw new Error(
      `${provider === "google" ? "Google" : "Apple"} is currently your only way to sign in. ` +
      `Set a password on your account first, then disconnect.`
    );
  }

  if (targetIdentity) {
    const auth = supabase.auth as unknown as {
      unlinkIdentity?: (identity: unknown) => Promise<{ error: { message: string } | null }>;
    };
    if (typeof auth.unlinkIdentity !== "function") {
      throw new Error("This app version cannot disconnect social logins. Please update the app.");
    }
    const { error: unlinkError } = await auth.unlinkIdentity(targetIdentity);
    if (unlinkError) {
      captureError("oauth-unlink", new Error(unlinkError.message), { provider, stage: "unlink-identity" });
      throw new Error(unlinkError.message || "Could not disconnect this account.");
    }

    // The locally-cached session still lists the identity we just removed, and
    // `getLinkedAccounts()` syncs from exactly that cached list — so without
    // this refresh the caller's own post-unlink reload would put the row
    // straight back. Best-effort: a failed refresh leaves a stale mirror row,
    // not a stale identity, and the next sign-in corrects it.
    try { await supabase.auth.refreshSession(); } catch { /* no-op */ }
  }
  // No matching identity means GoTrue never had one (or it is already gone) —
  // the mapping row is then stale and deleting it is exactly right.

  const { data: deleted, error } = await supabase
    .from("auth_methods")
    .delete()
    .eq("user_id", user.id)
    .eq("method_type", methodType)
    .select("id");

  if (error) throw error;

  // Zero rows with no error = the write was blocked or the row was already
  // gone. Never report success on an unverified write.
  if (!deleted || deleted.length === 0) {
    const stillThere = await supabase
      .from("auth_methods")
      .select("id")
      .eq("user_id", user.id)
      .eq("method_type", methodType)
      .limit(1);
    if (stillThere.data && stillThere.data.length > 0) {
      captureError("oauth-unlink", new Error("auth_methods delete affected 0 rows"), {
        provider,
        stage: "delete-verify",
      });
      throw new Error("Could not disconnect this account. Please try again.");
    }
  }
}

/**
 * Ensure the primary CNIC/NTN method is recorded in auth_methods.
 */
export async function ensurePrimaryMethod(identifier: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const { data: existing } = await supabase
    .from("auth_methods")
    .select("id")
    .eq("user_id", user.id)
    .eq("is_primary", true)
    .limit(1);

  if (existing && existing.length > 0) return;

  await supabase.from("auth_methods").insert({
    user_id: user.id,
    method_type: "cnic_password" as AuthMethodType,
    identifier,
    is_verified: true,
    is_primary: true,
  });
}
